import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useTranslation } from 'react-i18next';
import ListItemButton from '@mui/material/ListItemButton';
import { apiFetch } from '../api/client';
import { forumThreadUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import type { ForumCategory, ForumThread, Paginated } from '../api/types';

interface ListResponse<T> {
  data: T[];
}

export function Forum() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch<ListResponse<ForumCategory>>('/api/v1/forum/categories'),
      apiFetch<Paginated<ForumThread>>('/api/v1/forum/threads?include=user,category,car&per_page=20'),
    ])
      .then(([catsRes, threadsRes]) => {
        setCategories(catsRes.data);
        setThreads(threadsRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader />
      <Stack spacing={4}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant="h4">{t('forum.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/forum/new"
        >
          {t('forum.newThread')}
        </Button>
      </Stack>

      <Box>
        <Typography variant="h5" gutterBottom>{t('forum.categories')}</Typography>
        <List>
          {categories.map((cat) => (
            <ListItem key={cat.id} divider>
              <ListItemText
                primary={cat.name}
                secondary={`${cat.description ?? ''} — ${t('forum.threadCount', { count: cat.thread_count ?? 0 })}`}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box>
        <Typography variant="h5" gutterBottom>{t('forum.recentThreads')}</Typography>
        <List disablePadding>
          {threads.map((thread) => (
            <ListItem key={thread.id} divider disablePadding>
              <ListItemButton component={RouterLink} to={forumThreadUrl(thread)}>
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      useFlexGap
                      sx={{ flexWrap: 'wrap' }}
                    >
                      <Typography variant="subtitle1">{thread.title}</Typography>
                      {thread.is_pinned && <Chip size="small" label={t('forum.pinned')} color="primary" />}
                      {thread.category && (
                        <Chip size="small" label={thread.category.name} variant="outlined" />
                      )}
                      {thread.car && (
                        <Chip
                          size="small"
                          icon={<DirectionsCarIcon />}
                          label={thread.car.display_name}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <>
                      {thread.user?.name ?? 'Unknown'} — {thread.last_post_at ? new Date(thread.last_post_at).toLocaleString() : ''}
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      </Stack>
    </Container>
  );
}
